import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import {
  VideoIcon,
  LanguagesIcon,
  SparklesIcon,
  Upload,
  Mail,
  PlayCircle,
  Download,
  AlertCircle,
  Globe,
  Send,
} from "lucide-react";
import { useVideoProcessing } from "../hooks/useVideoProcessing";
import { useVideoDownload } from "../hooks/useVideoDownload";
import { useTranslations } from "../hooks/useTranslations";
import { SEOHead } from "../components/SEOHead";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { apiService, PaymentNotificationRequest } from "../services/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../components/Header.css";
import { getCaptionsLandingContent, getCaptionsLandingFaq, getCaptionsLandingSeo } from "../content/captionsLanding";
import { getCreatorsLandingSeo } from "../content/creatorsLanding";
import {
  getAudioTranslationPairByPath,
  getAudioTranslationLandingContent,
  getAudioTranslationPairLandingSeo,
  getAudioTranslationPairButtonLabel,
} from "../content/audioTranslation";
import {
  AUDIO_TRANSLATION_LANDING_PATH,
  FREE_CAPTIONS_LANDING_PATH,
  getBasePathFromUrlPath,
  getLocalizedPath,
  isAudioTranslationPairPath,
  isCreatorsLandingPath,
  isDubbingLandingFamilyPath,
  isDubbingLandingPath,
} from "../utils/localizedRoutes";
import {
  getDubbingLandingFaq,
  getDubbingLandingSeo,
  getDubbingPairLandingContent,
} from "../content/dubbingLanding";
import { getDubbingPairByPath } from "../content/dubbingLanguagePairs";
import { getLeadingHomeFaqEntries, homeFaqOverrides } from "../content/homeFaqOverrides";
import { getHomeHeroPromo } from "../content/homeHeroPromo";
import { getHomeFaqHeading } from "../content/homeFaqHeading";
import { getHomeDemoContent } from "../content/homeDemo";
import { getDubbingLandingDemoContent } from "../content/dubbingLandingDemo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

function PaymentMethodLogos() {
  const logoCardClassName =
    "flex h-10 flex-1 min-w-0 items-center justify-center rounded-md border border-gray-200 bg-white px-1 shadow-sm";
  const assetLogoClassName = "h-5 w-auto max-w-full object-contain";

  return (
    <div className="w-full">
      <div className="flex w-full items-center gap-2">
        <div className={logoCardClassName} title="Visa" aria-label="Visa">
          <svg
            viewBox="0 0 72 24"
            className="h-5 w-full max-w-[54px]"
            role="img"
            aria-hidden="true"
          >
            <text
              x="36"
              y="17"
              fill="#1A1F71"
              fontFamily="Arial, Helvetica, sans-serif"
              fontSize="17"
              fontWeight="900"
              letterSpacing="-1"
              textAnchor="middle"
            >
              VISA
            </text>
          </svg>
        </div>

        <div
          className={logoCardClassName}
          title="MasterCard"
          aria-label="MasterCard"
        >
          <img
            src="/payment-logos/mastercard.svg"
            alt="MasterCard"
            className={assetLogoClassName}
          />
        </div>

        <div
          className={logoCardClassName}
          title="American Express"
          aria-label="American Express"
        >
          <svg
            viewBox="0 0 86 28"
            className="h-5 w-full max-w-[64px]"
            role="img"
            aria-hidden="true"
          >
            <rect x="1.5" y="1.5" width="83" height="25" rx="3" fill="#016FD0" />
            <rect
              x="1.5"
              y="1.5"
              width="83"
              height="25"
              rx="3"
              fill="none"
              stroke="#0A4E9B"
              strokeWidth="1.2"
            />
            <text
              x="43"
              y="12.5"
              fill="#FFFFFF"
              fontFamily="Arial, Helvetica, sans-serif"
              fontSize="7.8"
              fontWeight="900"
              textAnchor="middle"
            >
              AMERICAN
            </text>
            <text
              x="43"
              y="20.2"
              fill="#FFFFFF"
              fontFamily="Arial, Helvetica, sans-serif"
              fontSize="7.8"
              fontWeight="900"
              textAnchor="middle"
            >
              EXPRESS
            </text>
          </svg>
        </div>

        <div className={logoCardClassName} title="Apple Pay" aria-label="Apple Pay">
          <img
            src="/payment-logos/apple-pay.svg"
            alt="Apple Pay"
            className={assetLogoClassName}
          />
        </div>

        <div className={logoCardClassName} title="Google Pay" aria-label="Google Pay">
          <img
            src="/payment-logos/google-pay.svg"
            alt="Google Pay"
            className={assetLogoClassName}
          />
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const MAX_VIDEO_FILE_SIZE_MB = 200;
  const MAX_VIDEO_DURATION_SECONDS = 30;
  const MAX_VIDEO_FILE_SIZE_BYTES = MAX_VIDEO_FILE_SIZE_MB * 1024 * 1024;
  const FIXED_PROCESSING_COST = 2;
  const REVOLUT_CHECKOUT_URL =
    "https://checkout.revolut.com/pay/2eeadf1b-2310-46da-a448-ec88e81e4ae0";
  const PROCESSING_RATE_TIERS = [
    { maxSeconds: 30, rate: 0.089 },
    { maxSeconds: 60, rate: 0.069 },
    { maxSeconds: 90, rate: 0.049 },
    { maxSeconds: 120, rate: 0.039 },
    { maxSeconds: 150, rate: 0.029 },
    { maxSeconds: 180, rate: 0.019 },
  ] as const;
  const navigate = useNavigate();
  const location = useLocation();
  const { t, getTranslation, currentLanguage, changeLanguage, availableLanguages } = useTranslations();
  const currentBasePath = getBasePathFromUrlPath(location.pathname);
  const isCreatorsLanding = isCreatorsLandingPath(location.pathname);
  const isCaptionsLanding = currentBasePath === FREE_CAPTIONS_LANDING_PATH;
  const isDubbingLanding = isDubbingLandingPath(location.pathname);
  const isDubbingLandingFamily = isDubbingLandingFamilyPath(location.pathname);
  const dubbingPair = currentBasePath ? getDubbingPairByPath(currentBasePath) : null;
  const audioTranslationPair = currentBasePath ? getAudioTranslationPairByPath(currentBasePath) : null;
  const isAudioTranslationPairPage = Boolean(
    currentBasePath && isAudioTranslationPairPath(currentBasePath)
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("");
  const [email, setEmail] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeError, setPromoCodeError] = useState("");
  const [promoCodeSuccess, setPromoCodeSuccess] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [basePrice, setBasePrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [emailError, setEmailError] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showEmailSentDialog, setShowEmailSentDialog] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processedVideoUrl, setProcessedVideoUrl] = useState("");
  const [processedFilename, setProcessedFilename] = useState("");
  const [fileValidationError, setFileValidationError] = useState("");
  
  const mode: "dubbing" | "subtitles" = isCaptionsLanding ? "subtitles" : "dubbing";
  const [needsTranslation, setNeedsTranslation] = useState(false);

  // Custom hooks
  const {
    isProcessing,
    progress,
    progressText,
    currentStep,
    error: processingError,
    taskId,
    isUploading,
    uploadProgress,
    startProcessing,
    stopProcessing,
  } = useVideoProcessing();

  const {
    isDownloading,
    downloadVideo,
    downloadDirect,
    error: downloadError,
  } = useVideoDownload();

  // FAQ data - map from translations
  const audioTranslationPairLandingContent = audioTranslationPair
    ? getAudioTranslationPairLandingSeo(currentLanguage, audioTranslationPair)
    : null;
  const pairLandingContent = dubbingPair
    ? getDubbingPairLandingContent(currentLanguage, dubbingPair)
    : null;
  const currentFaqOverrides = homeFaqOverrides[currentLanguage] ?? {};
  const localizedHomeFaq = [...getLeadingHomeFaqEntries(currentLanguage), ...t.faq.questions];
  const currentFaq = isCaptionsLanding
    ? getCaptionsLandingFaq(currentLanguage)
    : audioTranslationPairLandingContent
    ? audioTranslationPairLandingContent.faq
    : pairLandingContent
    ? pairLandingContent.faq
    : isDubbingLanding
    ? getDubbingLandingFaq(currentLanguage)
    : localizedHomeFaq.map((faq, index) => currentFaqOverrides[index] ?? faq);
  const faqData = currentFaq.map((faq, index) => ({
    id: `item-${index + 1}`,
    question: faq.question,
    answer: faq.answer,
  }));

  const defaultLanguageLabels: Record<string, string> = {
    ar: "Arabic",
    bg: "Bulgarian",
    zh: "Chinese",
    hr: "Croatian",
    cs: "Czech",
    da: "Danish",
    nl: "Dutch",
    en: "English",
    et: "Estonian",
    fi: "Finnish",
    fr: "French",
    de: "German",
    el: "Greek",
    hu: "Hungarian",
    ga: "Irish",
    it: "Italian",
    ja: "Japanese",
    ko: "Korean",
    lv: "Latvian",
    lt: "Lithuanian",
    mt: "Maltese",
    pl: "Polish",
    pt: "Portuguese",
    ro: "Romanian",
    ru: "Russian",
    sk: "Slovak",
    sl: "Slovenian",
    es: "Spanish",
    sv: "Swedish",
  };

  const languageLabel = (languageCode: string) =>
    (t.languageOptions as Record<string, string>)?.[languageCode] ||
    defaultLanguageLabels[languageCode] ||
    languageCode.toUpperCase();
  const presetSourceLanguage = audioTranslationPair?.sourceCode || dubbingPair?.sourceCode || "";
  const presetTargetLanguage = audioTranslationPair?.targetCode || dubbingPair?.targetCode || "";
  const effectiveSourceLanguage = sourceLanguage || presetSourceLanguage;
  const effectiveTargetLanguage = targetLanguage || presetTargetLanguage;

  // Language options (sorted alphabetically by English name)
  const languages = [
    { value: "ar", label: languageLabel("ar") }, // Arabic
    { value: "bg", label: languageLabel("bg") }, // Bulgarian
    { value: "zh", label: languageLabel("zh") }, // Chinese
    { value: "hr", label: languageLabel("hr") }, // Croatian
    { value: "cs", label: languageLabel("cs") }, // Czech
    { value: "da", label: languageLabel("da") }, // Danish
    { value: "nl", label: languageLabel("nl") }, // Dutch
    { value: "en", label: languageLabel("en") }, // English
    { value: "et", label: languageLabel("et") }, // Estonian
    { value: "fi", label: languageLabel("fi") }, // Finnish
    { value: "fr", label: languageLabel("fr") }, // French
    { value: "de", label: languageLabel("de") }, // German
    { value: "el", label: languageLabel("el") }, // Greek
    { value: "hu", label: languageLabel("hu") }, // Hungarian
    { value: "ga", label: languageLabel("ga") }, // Irish
    { value: "it", label: languageLabel("it") }, // Italian
    { value: "ja", label: languageLabel("ja") }, // Japanese
    { value: "ko", label: languageLabel("ko") }, // Korean
    { value: "lv", label: languageLabel("lv") }, // Latvian
    { value: "lt", label: languageLabel("lt") }, // Lithuanian
    { value: "mt", label: languageLabel("mt") }, // Maltese
    { value: "pl", label: languageLabel("pl") }, // Polish
    { value: "pt", label: languageLabel("pt") }, // Portuguese
    { value: "ro", label: languageLabel("ro") }, // Romanian
    { value: "ru", label: languageLabel("ru") }, // Russian
    { value: "sk", label: languageLabel("sk") }, // Slovak
    { value: "sl", label: languageLabel("sl") }, // Slovenian
    { value: "es", label: languageLabel("es") }, // Spanish
    { value: "sv", label: languageLabel("sv") }, // Swedish
  ];

  // Mock function to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  // Promo codes configuration
  const promoCodes: Record<string, number> = {
    ilovevevi: 100, // 100% discount
    missyou: 50,
    hateyou: 50,
    needyou: 50,
    lovehard: 50,
    nowords: 50,
    allyours: 50,
    goaway: 50,
    toomuch: 50,
    coldeyes: 50,
    holdtight: 50,
    forgetme: 50,
    onlyyou: 50,
    whyme: 50,
    sodone: 50,
    dreamon: 50,
    crymore: 50,
    nopebro: 50,
    fakelove: 50,
    overyou: 50,
    tryhard: 50,
    lolsure: 50,
    nextpls: 50,
    sadvibes: 50,
    borednow: 50,
    okboom: 50,
    dramaqueen: 50,
    chillbro: 50,
    bigmad: 50,
    zerofeels: 50,
    byefelicia: 50,
    SmarPoland: 25,
    AroundPRStudio: 25,
    smarglobal: 35,
  };

  // Validate promo code
  const validatePromoCode = (
    code: string
  ): { isValid: boolean; discount: number } => {
    const normalizedCode = code.trim().toLowerCase();
    if (import.meta.env.DEV) {
      console.log("Validating promo code:", {
        original: code,
        normalized: normalizedCode,
      });
    }
    const discount = promoCodes[normalizedCode];
    if (import.meta.env.DEV) {
      console.log("Found discount:", discount);
    }

    if (discount !== undefined) {
      return { isValid: true, discount };
    }

    return { isValid: false, discount: 0 };
  };

  // Calculate price with discount
  const getRateForDuration = (durationInSeconds: number): number => {
    const normalizedDuration = Math.max(0, Math.ceil(durationInSeconds));

    return (
      PROCESSING_RATE_TIERS.find(({ maxSeconds }) => normalizedDuration <= maxSeconds)?.rate ||
      PROCESSING_RATE_TIERS[PROCESSING_RATE_TIERS.length - 1].rate
    );
  };

  const calculatePrice = (
    durationInSeconds: number,
    discountPercent: number = 0
  ): number => {
    const appliedRate = getRateForDuration(durationInSeconds);
    const basePrice = Math.round(durationInSeconds * appliedRate * 100) / 100;
    const discountAmount = (basePrice * discountPercent) / 100;
    const finalPrice = Math.max(0, basePrice - discountAmount);
    return Math.round(finalPrice * 100) / 100; // Round to 2 decimal places
  };

  // Recalculate final price when discount changes
  const recalculateFinalPrice = (
    basePrice: number,
    discountPercent: number
  ): number => {
    const discountAmount = (basePrice * discountPercent) / 100;
    const finalPrice = Math.max(0, basePrice - discountAmount);
    return Math.round(finalPrice * 100) / 100;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins} ${t.common.minutes} ${secs} ${t.common.seconds}`;
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = async (file: File) => {
    // Clear previous validation errors
    setFileValidationError("");

    // Check file type
    if (!file.type.startsWith("video/")) {
      setFileValidationError(t.upload.fileTypeError);
      return;
    }

    // Check file size limit
    if (file.size > MAX_VIDEO_FILE_SIZE_BYTES) {
      const fileSizeInMB = (file.size / 1024 / 1024).toFixed(2);
      setFileValidationError(
        getTranslation("upload.fileSizeError", { size: fileSizeInMB })
      );
      return;
    }

    // Check video duration limit
    try {
      const duration = await getVideoDuration(file);
      if (duration > MAX_VIDEO_DURATION_SECONDS) {
        setFileValidationError(
          getTranslation("upload.durationError", { duration: Math.ceil(duration) })
        );
        return;
      }

      // If all validations pass, set the file
      setSelectedFile(file);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error getting video duration:", error);
      }
      setFileValidationError(t.upload.fileReadError);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setFileValidationError(""); // Clear validation errors
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0] instanceof File) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileValidationError(""); // Clear validation errors
    const file = e.target.files?.[0];
    if (file && file instanceof File) {
      handleFileSelect(file);
    }
  };

  const openVideoUploadDialog = () => {
    const uploadInput = document.getElementById("video-upload") as HTMLInputElement | null;
    uploadInput?.click();
  };

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError(t.payment.enterPromoCode);
      setPromoCodeSuccess("");
      setDiscountPercentage(0);
      return;
    }

    setIsValidatingPromo(true);
    setPromoCodeError("");
    setPromoCodeSuccess("");

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    const validation = validatePromoCode(promoCode);

    if (validation.isValid) {
      setDiscountPercentage(validation.discount);
      setPromoCodeSuccess(
        getTranslation("payment.validPromoCode", {
          discount: validation.discount.toString(),
        })
      );
      setPromoCodeError("");

      // Recalculate final price if we have base price
      if (basePrice > 0) {
        const newFinalPrice = recalculateFinalPrice(
          basePrice,
          validation.discount
        );
        if (import.meta.env.DEV) {
          console.log("Promo code price recalculation:", {
            basePrice,
            discount: validation.discount,
            newFinalPrice,
          });
        }
        setFinalPrice(newFinalPrice);
        (window as any).videoPrice = newFinalPrice;
        (window as any).discountPercentage = validation.discount;
      }
    } else {
      setPromoCodeError(t.payment.invalidPromoCode);
      setPromoCodeSuccess("");
      setDiscountPercentage(0);

      // Reset to base price if we have it
      if (basePrice > 0) {
        setFinalPrice(basePrice);
        (window as any).videoPrice = basePrice;
        (window as any).discountPercentage = 0;
      }
    }

    setIsValidatingPromo(false);
  };

  const handleConvert = async () => {
    if (isAudioTranslationPairPage && !selectedFile) {
      openVideoUploadDialog();
      return;
    }

    if (!selectedFile) return;

    if (!isCaptionsLanding) {
      try {
        await apiService.notifyReplaceAudio({ filename: selectedFile.name || "video.mp4" });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn("Failed to notify replace-audio click:", error);
        }
      }
    }

    const duration = await getVideoDuration(selectedFile);
    const basePriceValue = isCaptionsLanding ? 0 : FIXED_PROCESSING_COST;
    const finalPriceValue = recalculateFinalPrice(
      basePriceValue,
      discountPercentage
    );

    if (import.meta.env.DEV) {
      console.log("Price calculation:", {
        duration,
        basePriceValue,
        discountPercentage,
        finalPriceValue,
      });
    }

    // Store data for dialog display
    (window as any).videoDuration = duration;
    (window as any).videoPrice = finalPriceValue;
    (window as any).discountPercentage = discountPercentage;
    (window as any).basePrice = basePriceValue;

    // Update state
    setBasePrice(basePriceValue);
    setFinalPrice(finalPriceValue);

    setShowPaymentDialog(true);
  };

  const handlePayment = async () => {
    // Validate email
    if (!email) {
      setEmailError(t.payment.enterEmail);
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t.payment.enterValidEmail);
      return;
    }

    setEmailError(""); // Clear any previous errors

    if (!selectedFile) {
      alert("Please select a video file");
      return;
    }

    if (mode === "dubbing") {
      if (!effectiveTargetLanguage || !effectiveSourceLanguage) {
        alert("Please select both source and target languages");
        return;
      }
    }
    
    if (mode === "subtitles" && needsTranslation) {
      if (!targetLanguage) {
        alert("Please select translation language");
        return;
      }
    }

    setShowPaymentDialog(false);

    if (isCaptionsLanding) {
      try {
        const duration = await getVideoDuration(selectedFile);

        setShowResultDialog(false);
        setShowEmailSentDialog(false);
        setShowProgressDialog(true);

        await startProcessing({
          video: selectedFile,
          email,
          duration,
          cost: 0,
          source_language: effectiveSourceLanguage,
          target_language: needsTranslation ? targetLanguage : effectiveSourceLanguage,
          add_subtitles: true,
        });
      } catch (error) {
        setShowProgressDialog(false);
        if (import.meta.env.DEV) {
          console.error("Failed to start free subtitles processing:", error);
        }
        alert(t.upload.fileReadError);
      }

      return;
    }

    try {
      const popupWidth = 520;
      const popupHeight = 760;
      const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
      const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
      const popupFeatures = [
        "popup=yes",
        `width=${popupWidth}`,
        `height=${popupHeight}`,
        `left=${left}`,
        `top=${top}`,
        "resizable=yes",
        "scrollbars=yes",
      ].join(",");

      const checkoutWindow = window.open(
        REVOLUT_CHECKOUT_URL,
        "revolutCheckout",
        popupFeatures
      );

      if (!checkoutWindow) {
        window.location.href = REVOLUT_CHECKOUT_URL;
        return;
      }

      checkoutWindow.focus();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Failed to open Revolut checkout:", error);
      }
      window.location.href = REVOLUT_CHECKOUT_URL;
    }
  };

  // Handle processing completion
  useEffect(() => {
    if (!isProcessing && taskId && currentStep === "complete") {
      setShowProgressDialog(false);

      if (isCaptionsLanding) {
        setShowResultDialog(false);
        setShowEmailSentDialog(true);
        return;
      }

      setShowResultDialog(true);
      setProcessedFilename(`video_dubbed_${taskId}.mp4`);

      // Use real processed video URL if available
      const realVideoUrl = (window as any).processedVideoUrl;
      if (realVideoUrl) {
        setProcessedVideoUrl(realVideoUrl);
      } else {
        // Fallback: construct download URL using the same API base URL
        const downloadUrl = apiService.getDownloadUrl(taskId);
        setProcessedVideoUrl(downloadUrl);
      }
    }
  }, [isCaptionsLanding, isProcessing, taskId, currentStep]);

  // Handle processing errors
  useEffect(() => {
    if (processingError) {
      setShowProgressDialog(false);
      alert(`Processing error: ${processingError}`);
    }
  }, [processingError]);

  const canConvert = 
    mode === "subtitles" && !needsTranslation
      ? selectedFile && sourceLanguage && !fileValidationError
      : mode === "subtitles" && needsTranslation
      ? selectedFile && sourceLanguage && targetLanguage && !fileValidationError
      : selectedFile && effectiveSourceLanguage && effectiveTargetLanguage && !fileValidationError;
  const isPrimaryCtaEnabled = isAudioTranslationPairPage ? !fileValidationError : canConvert;

  const dubbingLandingSeo =
    audioTranslationPairLandingContent ||
    pairLandingContent ||
    getDubbingLandingSeo(currentLanguage);
  const captionsLandingContent = getCaptionsLandingContent(currentLanguage);
  const audioTranslationLandingContent = getAudioTranslationLandingContent(currentLanguage);
  const captionsLandingSeo = getCaptionsLandingSeo(currentLanguage);
  const creatorsLandingSeo = getCreatorsLandingSeo(currentLanguage);
  const homePromoSubtitle = getHomeHeroPromo(currentLanguage);
  const homeFaqHeading = getHomeFaqHeading(currentLanguage);
  const homeDemoContent = getHomeDemoContent(currentLanguage);
  const dubbingLandingDemoContent = getDubbingLandingDemoContent(currentLanguage);
  const demoContent =
    isDubbingLandingFamily || isAudioTranslationPairPage
      ? dubbingLandingDemoContent
      : homeDemoContent;
  const captionsWorkflowCopy = captionsLandingContent.workflow;
  const faqHeading = isCaptionsLanding
    ? {
        title: captionsLandingContent.faqHeadingTitle,
        subtitle: captionsLandingContent.faqHeadingSubtitle,
      }
    : homeFaqHeading;
  const heroTitle = isCaptionsLanding
    ? captionsLandingContent.heroTitle
    : isDubbingLandingFamily || isAudioTranslationPairPage
    ? dubbingLandingSeo.heroTitle
    : t.header.title;
  const heroDescription = isCaptionsLanding
    ? captionsLandingContent.heroDescription
    : isAudioTranslationPairPage
    ? audioTranslationPairLandingContent?.heroDescription || getDubbingLandingSeo(currentLanguage).description
    : isDubbingLandingFamily || isAudioTranslationPairPage
    ? dubbingLandingSeo.description
    : homePromoSubtitle;
  const maxUploadFileSizeLabel = t.upload.maxFileSize;
  const maxUploadDurationLabel = t.upload.maxDuration;
  const heroTitleStyle = { fontSize: "48px", lineHeight: "1.02" as const };
  const termsUrl = getLocalizedPath("/terms", currentLanguage);
  const dialogBasePrice =
    basePrice || (typeof window !== "undefined" ? Number((window as any).basePrice || 0) : 0);
  const dialogFinalPrice =
    finalPrice || (typeof window !== "undefined" ? Number((window as any).videoPrice || 0) : 0);
  const captionsDialogCopy = captionsLandingContent.popup;
  const captionsEmailSentCopy = captionsLandingContent.success;
  const audioTranslationPairBreadcrumbLabel = audioTranslationPair
    ? getAudioTranslationPairButtonLabel(currentLanguage, audioTranslationPair)
    : "";
  const pageTopPadding = isAudioTranslationPairPage ? "112px" : "48px";
  const pageMainClassName = isAudioTranslationPairPage
    ? "vevi-page-main py-6 sm:py-8 lg:py-10"
    : "vevi-page-main py-8";
  const heroTopPadding = isAudioTranslationPairPage ? "0px" : "36px";

  const resetForm = () => {
    setSelectedFile(null);
    setSourceLanguage(presetSourceLanguage);
    setTargetLanguage(presetTargetLanguage);
    setEmail("");
    setPromoCode("");
    setPromoCodeError("");
    setPromoCodeSuccess("");
    setDiscountPercentage(0);
    setBasePrice(0);
    setFinalPrice(0);
    setFileValidationError("");
    setNeedsTranslation(false);
  };

  useEffect(() => {
    if (audioTranslationPair) {
      setSourceLanguage(audioTranslationPair.sourceCode);
      setTargetLanguage(audioTranslationPair.targetCode);
      return;
    }

    if (dubbingPair) {
      setSourceLanguage(dubbingPair.sourceCode);
      setTargetLanguage(dubbingPair.targetCode);
    }
  }, [audioTranslationPair, dubbingPair]);

  return (
    <>
      <SEOHead
        pageKey={isDubbingLandingFamily || isAudioTranslationPairPage ? "dubbingLanding" : "home"}
        overrideSeo={
          isCaptionsLanding
            ? {
                title: captionsLandingSeo.seoTitle,
                description: captionsLandingSeo.seoDescription,
                keywords: captionsLandingSeo.keywords,
              }
            : isDubbingLandingFamily || isAudioTranslationPairPage
            ? dubbingLandingSeo
            : isCreatorsLanding
            ? creatorsLandingSeo
            : undefined
        }
        canonicalPathOverride={
          (isCaptionsLanding ||
            isDubbingLandingFamily ||
            isAudioTranslationPairPage ||
            isCreatorsLanding) &&
          currentBasePath
            ? currentBasePath
            : undefined
        }
      />
      <Header />
      <main className="min-h-screen vevi-site-background" style={{ paddingTop: pageTopPadding }}>
        <div className="vevi-page-grid">
        <div className={pageMainClassName}>
        {/* Hero Section */}
        <div className="mb-12" style={{ paddingTop: heroTopPadding }}>
          {isAudioTranslationPairPage && audioTranslationPair && (
            <nav
              aria-label="Breadcrumb"
              className="mb-4 flex flex-wrap items-center gap-3 text-sm font-medium text-white"
            >
              <Link
                to={getLocalizedPath("/", currentLanguage)}
                className="inline-flex items-center transition-colors hover:text-purple-100"
              >
                {audioTranslationLandingContent.breadcrumbHomeLabel}
              </Link>
              <span className="text-purple-200/80">|</span>
              <Link
                to={getLocalizedPath(AUDIO_TRANSLATION_LANDING_PATH, currentLanguage)}
                className="transition-colors hover:text-purple-100"
              >
                {audioTranslationLandingContent.breadcrumbCurrentLabel}
              </Link>
              <span className="text-purple-200/80">|</span>
              <span className="text-white">{audioTranslationPairBreadcrumbLabel}</span>
            </nav>
          )}

          <div className="text-center">
            <h1 className="text-white mb-4" style={heroTitleStyle}>{heroTitle}</h1>
            {heroDescription && (
              <p className="text-purple-100" style={{ whiteSpace: "pre-line" }}>
                {heroDescription}
              </p>
            )}
          </div>
        </div>

        {/* Main card */}
        <Card className="w-full bg-white/95 backdrop-blur-sm shadow-xl">
          <CardContent className="p-8">
            {/* Step 1: Video Upload */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                  1
                </div>
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <VideoIcon className="w-5 h-5" aria-hidden="true" />
                  {t.steps.uploadVideo}
                </h2>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-4 md:p-8 text-center transition-colors max-w-full overflow-hidden ${isDragOver
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-300 bg-gray-50"
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {selectedFile ? (
                  <div className="text-green-600 max-w-full">
                    <VideoIcon className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2" />
                    <p
                      className="text-xs md:text-sm truncate w-full whitespace-nowrap overflow-hidden"
                      title={selectedFile.name}
                    >
                      {t.upload.selectedFile} {selectedFile.name}
                    </p>
                    <p className="text-xs md:text-sm text-gray-700">
                      {t.upload.size}{" "}
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : fileValidationError ? (
                  <div className="text-red-600 max-w-full">
                    <AlertCircle className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2" />
                    <p className="text-xs md:text-sm text-center">
                      {fileValidationError}
                    </p>
                  </div>
                ) : (
                  <div className="text-gray-700">
                    <Upload className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-2 md:mb-4" />
                    <p className="mb-1 md:mb-2 text-sm md:text-base font-medium">
                      {t.upload.dragAndDrop}
                    </p>

                    <div className="text-xs md:text-sm text-gray-600 space-y-1">
                      <p>{t.upload.supportedFormats}</p>
                      <p>{maxUploadFileSizeLabel}</p>
                      <p>{maxUploadDurationLabel}</p>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileInput}
                  className="hidden"
                  id="video-upload"
                  aria-label="Upload video file"
                />
                <label
                  htmlFor="video-upload"
                  className="inline-block"
                >
                  <Button
                    variant="outline"
                    className="mt-2 md:mt-4 cursor-pointer text-sm md:text-base"
                    asChild
                  >
                    <span role="button" tabIndex={0} aria-label="Choose video file to upload">
                      {t.upload.chooseFile}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Step 2: Language Selection */}
            {(mode === "dubbing" || mode === "subtitles") && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                    2
                  </div>
                  <h2 className="flex items-center gap-2 text-xl font-semibold">
                    <LanguagesIcon className="w-5 h-5" aria-hidden="true" />
                    {t.steps.selectLanguages}
                  </h2>
                </div>

                {/* Dubbing mode */}
                {mode === "dubbing" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-2">
                          {t.languages.sourceAudioLanguage}
                        </label>
                        <Select
                          value={sourceLanguage}
                          onValueChange={setSourceLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-2">
                          {t.languages.targetAudioLanguage}
                        </label>
                        <Select
                          value={targetLanguage}
                          onValueChange={setTargetLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}

                {/* Subtitles mode */}
                {mode === "subtitles" && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm text-gray-600 mb-2">
                        {t.languages.sourceAudioLanguage}
                      </label>
                      <Select
                        value={sourceLanguage}
                        onValueChange={setSourceLanguage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.languages.selectLanguage} />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map((lang) => (
                            <SelectItem key={lang.value} value={lang.value}>
                              {lang.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="mb-4 flex items-center space-x-2">
                      <Checkbox
                        id="needs-translation"
                        checked={needsTranslation}
                        onCheckedChange={(checked) => {
                          setNeedsTranslation(checked === true);
                          if (!checked) {
                            setTargetLanguage("");
                          }
                        }}
                      />
                      <label
                        htmlFor="needs-translation"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {isCaptionsLanding
                          ? captionsWorkflowCopy.needsTranslation
                          : t.services?.needsTranslation || "Need translation?"}
                      </label>
                    </div>

                    {/* Если чекбокс активен - показываем выбор языка перевода */}
                    {needsTranslation && (
                      <div className="mb-4">
                        <label className="block text-sm text-gray-600 mb-2">
                          {isCaptionsLanding
                            ? captionsWorkflowCopy.translationLanguage
                            : t.services?.translationLanguage || "Translation language"}
                        </label>
                        <Select
                          value={targetLanguage}
                          onValueChange={setTargetLanguage}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t.languages.selectLanguage} />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map((lang) => (
                              <SelectItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Conversion */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center" aria-hidden="true">
                  3
                </div>
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <SparklesIcon className="w-5 h-5" aria-hidden="true" />
                  {t.steps.conversionResult}
                </h2>
              </div>

              <div className="text-center">
                <Button
                  onClick={handleConvert}
                  disabled={!isPrimaryCtaEnabled}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-3"
                  aria-label={
                    mode === "subtitles"
                      ? isCaptionsLanding
                        ? captionsWorkflowCopy.createSubtitles
                        : t.conversion?.createSubtitles || "Create Subtitles"
                      : t.conversion.replaceAudio
                  }
                  aria-disabled={!isPrimaryCtaEnabled}
                >
                  {mode === "subtitles"
                    ? isCaptionsLanding
                      ? captionsWorkflowCopy.createSubtitles
                      : t.conversion?.createSubtitles || "Create Subtitles"
                    : t.conversion.replaceAudio}
                </Button>

                {!isPrimaryCtaEnabled && (
                  <p className="text-sm text-gray-600 mt-2">
                    {fileValidationError
                      ? isCaptionsLanding
                        ? captionsWorkflowCopy.fixValidationError
                        : t.conversion.fixValidationError
                      : mode === "subtitles" && !sourceLanguage
                      ? t.languages.sourceAudioLanguage
                      : mode === "subtitles" && !needsTranslation
                      ? isCaptionsLanding
                        ? captionsWorkflowCopy.uploadVideoForSubtitles
                        : t.conversion?.uploadVideoForSubtitles || "Upload a video to create subtitles"
                      : mode === "subtitles" && needsTranslation && !targetLanguage
                      ? isCaptionsLanding
                        ? captionsWorkflowCopy.selectTranslationLanguage
                        : t.conversion?.selectTranslationLanguage || "Select translation language"
                      : t.conversion.uploadVideoAndSelectLanguages}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {!isCaptionsLanding && (
          <section className="mt-16" aria-label={demoContent.title}>
            <div className="text-center mb-8">
              <h2 className="text-white text-3xl mb-2">{demoContent.title}</h2>
              <p className="text-purple-100 max-w-3xl mx-auto">
                {demoContent.description}
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8">
                <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-950 shadow-sm">
                  <video
                    controls
                    controlsList="nodownload"
                    className="w-full aspect-video object-contain bg-black"
                    aria-label="VEVI demo video showing voice translation in action"
                  >
                    <source src="/VEVI_promo.mp4" type="video/mp4" />
                    Your browser does not support video
                  </video>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-sm">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 min-w-0">
                    <h3 className="text-slate-900 mb-2 break-words text-base font-semibold">
                      {demoContent.beforeLabel}
                    </h3>
                    <p className="text-slate-600 break-words">
                      {demoContent.beforeDescription}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 min-w-0">
                    <h3 className="text-emerald-900 mb-2 break-words text-base font-semibold">
                      {demoContent.afterLabel}
                    </h3>
                    <p className="text-emerald-700 break-words">
                      {demoContent.afterDescription}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}
        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            {isCaptionsLanding ? (
              <>
                <DialogHeader>
                  <DialogTitle>{captionsDialogCopy.title}</DialogTitle>
                  <p className="text-sm text-gray-500">
                    {captionsDialogCopy.description}
                  </p>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      {captionsDialogCopy.emailLabel}
                    </label>
                    <Input
                      type="email"
                      placeholder={captionsDialogCopy.emailPlaceholder}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                      }}
                      className={`w-full ${emailError ? "border-red-500" : ""}`}
                    />
                    {emailError && (
                      <p className="text-red-500 text-sm mt-1">{emailError}</p>
                    )}
                  </div>

                  <div className="space-y-2 rounded-xl bg-emerald-50/80 p-4 text-sm text-gray-700">
                    {captionsDialogCopy.benefits.map((benefit) => (
                      <p key={benefit}>✓ {benefit}</p>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowPaymentDialog(false)}
                      className="flex-1"
                      aria-label={captionsDialogCopy.cancel}
                    >
                      {captionsDialogCopy.cancel}
                    </Button>
                    <Button
                      onClick={handlePayment}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                      aria-label={captionsDialogCopy.submit}
                    >
                      {captionsDialogCopy.submit}
                    </Button>
                  </div>

                  <div className="text-center text-xs leading-relaxed text-gray-500">
                    <p>
                      {captionsDialogCopy.legalTextBeforeLink}{" "}
                      <a href={termsUrl} className="underline underline-offset-2 hover:text-gray-700">
                        {t.footer?.termsOfUse || "Terms of Use"}
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Conversion Payment</DialogTitle>
                  <p className="text-sm text-gray-500">
                    This is a one-time payment, not a subscription, no recurring charges.
                  </p>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="font-semibold text-lg">Processing Cost</h3>
                        <span className="text-2xl font-semibold text-purple-600">
                          ${dialogFinalPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="w-full">
                      <PaymentMethodLogos />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      {t.payment.emailToReceiveResult}
                    </label>
                    <Input
                      type="email"
                      placeholder={t.payment.emailPlaceholder}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                      }}
                      className={`w-full ${emailError ? "border-red-500" : ""}`}
                    />
                    {emailError && (
                      <p className="text-red-500 text-sm mt-1">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      {t.payment.promoCode}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder={t.payment.promoCodePlaceholder}
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          setPromoCodeError("");
                          setPromoCodeSuccess("");
                          setDiscountPercentage(0);

                          if (basePrice > 0) {
                            setFinalPrice(basePrice);
                            (window as any).videoPrice = basePrice;
                            (window as any).discountPercentage = 0;
                          }
                        }}
                        className={`flex-1 ${promoCodeError
                            ? "border-red-500"
                            : promoCodeSuccess
                              ? "border-green-500"
                              : ""
                          }`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePromoCodeValidation}
                        disabled={isValidatingPromo || !promoCode.trim()}
                        className="px-4"
                        aria-label="Validate promo code"
                      >
                        {isValidatingPromo ? t.payment.checking : t.payment.check}
                      </Button>
                    </div>
                    {promoCodeError && (
                      <p className="text-red-500 text-sm mt-1">{promoCodeError}</p>
                    )}
                    {promoCodeSuccess && (
                      <p className="text-green-600 text-sm mt-1">
                        {promoCodeSuccess}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-gray-700">
                    <p>⚡ Automatic translation + natural lip-sync</p>
                    <p>🎥 Your original Short stays intact</p>
                    <p>🚀 Ready in minutes — no editing needed</p>
                    <p>📩 Finished video delivered to your email also</p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowPaymentDialog(false)}
                      className="flex-1"
                      aria-label="Cancel payment"
                    >
                      Stop
                    </Button>
                    <Button
                      onClick={handlePayment}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                      aria-label="Continue to payment"
                    >
                      Go Global!
                    </Button>
                  </div>

                  <div className="space-y-2 text-center text-xs leading-relaxed text-gray-500">
                    <p>Guaranteed safe & secure checkout | Powered by Revolut Pay</p>
                    <p>
                      By clicking "Go Global!" you agree to the{" "}
                      <a href={termsUrl} className="underline underline-offset-2 hover:text-gray-700">
                        Terms of Use
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Progress Dialog */}
        <Dialog open={showProgressDialog} onOpenChange={() => { }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.progress.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SparklesIcon className="w-8 h-8 text-purple-600" />
                </div>
                <p className="text-lg">{progressText}</p>
                {currentStep && (
                  <p className="text-sm text-blue-600">
                    {t.progress.step} {currentStep}
                  </p>
                )}
              </div>

              {/* Показываем прогресс-бар только при загрузке файла */}
              {isUploading ? (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="w-full" />
                  <p className="text-sm text-gray-700 text-center font-medium">
                    {uploadProgress}%
                  </p>
                </div>
              ) : (
                // После загрузки - просто крутящийся лоадер без процентов
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              )}

              {processingError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {t.progress.error}
                    </span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{processingError}</p>
                </div>
              )}

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p>{t.progress.uploadingAndExtracting}</p>
                <p>{t.progress.transcribingAndTranslating}</p>
                <p>{t.progress.synthesizingAudio}</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    stopProcessing();
                    setShowProgressDialog(false);
                  }}
                  className="flex-1"
                  aria-label="Cancel video processing"
                >
                  {t.payment.cancel}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Result Dialog */}
        <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t.result.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlayCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-green-600">{t.result.conversionCompleted}</p>
              </div>

              {/* Video Player */}
              <div className="space-y-2">
                <p className="text-sm text-gray-600 text-center">
                  {t.result.processedVideoDescription}
                </p>
                <div className="bg-black rounded-lg overflow-hidden">
                  <video
                    src={processedVideoUrl}
                    controls
                    controlsList="nodownload"
                    preload="metadata"
                    crossOrigin="anonymous"
                    playsInline
                    webkit-playsinline="true"
                    className="w-full h-64 object-contain"
                    poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMDAwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiNmZmYiIGR5PSIuM2VtIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Qcm9jZXNzZWQgVmlkZW88L3RleHQ+PC9zdmc+"
                    aria-label="Your processed video with voice translation"
                    onError={(e) => {
                      if (import.meta.env.DEV) {
                        console.error("Video load error:", e);
                        console.error("Video URL:", processedVideoUrl);
                      }
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.style &&
                        (e.currentTarget.nextElementSibling.style.display = "block");
                    }}
                    onLoadStart={() => {
                      if (import.meta.env.DEV) {
                        console.log("Video loading started:", processedVideoUrl);
                      }
                    }}
                    onCanPlay={() => {
                      if (import.meta.env.DEV) {
                        console.log("Video can play:", processedVideoUrl);
                      }
                    }}
                  >
                    Your browser does not support video
                  </video>
                  <div className="hidden w-full h-64 flex items-center justify-center text-white bg-gray-800">
                    <div className="text-center">
                      <p className="mb-2">{t.result.videoNotAvailable}</p>
                      <p className="text-sm text-gray-200">
                        {t.result.useDownloadButton}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  setShowEmailSentDialog(true);
                }}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                aria-label="Pay and download processed video"
              >
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                {t.result.payAndDownload}
              </Button>
            </div>

            {downloadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t.result.downloadError}
                  </span>
                </div>
                <p className="text-sm text-red-600 mt-1">{downloadError}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Sent Dialog */}
        <Dialog
          open={showEmailSentDialog}
          onOpenChange={setShowEmailSentDialog}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {isCaptionsLanding ? captionsEmailSentCopy.title : t.emailSent.title}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-lg text-green-600 mb-2">
                  {isCaptionsLanding ? captionsEmailSentCopy.message : t.emailSent.videoOnWay}
                </p>
                <p className="text-sm text-gray-600">
                  {isCaptionsLanding ? captionsEmailSentCopy.checkInbox : t.emailSent.checkInbox}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="mb-2 text-sm font-semibold">
                  {isCaptionsLanding ? captionsEmailSentCopy.whatsNext : t.emailSent.whatsNext}
                </h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    {isCaptionsLanding
                      ? captionsEmailSentCopy.checkEmail.replace("{email}", email)
                      : getTranslation("emailSent.checkEmail", { email })}
                  </p>
                  <p>
                    {isCaptionsLanding ? captionsEmailSentCopy.checkSpam : t.emailSent.checkSpam}
                  </p>
                  <p>
                    {isCaptionsLanding
                      ? captionsEmailSentCopy.useSubtitles
                      : t.emailSent.downloadAndEnjoy}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailSentDialog(false)}
                  className="flex-1"
                  aria-label={isCaptionsLanding ? captionsEmailSentCopy.close : t.emailSent.close}
                >
                  {isCaptionsLanding ? captionsEmailSentCopy.close : t.emailSent.close}
                </Button>
                <Button
                  onClick={() => {
                    setShowEmailSentDialog(false);
                    setShowResultDialog(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  aria-label={
                    isCaptionsLanding
                      ? captionsEmailSentCopy.processAnother
                      : t.emailSent.processAnotherVideo
                  }
                >
                  {isCaptionsLanding
                    ? captionsEmailSentCopy.processAnother
                    : t.emailSent.processAnotherVideo}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* FAQ Section */}
        <section className="mt-16" aria-label="FAQ">
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">{faqHeading.title}</h2>
            <p className="text-purple-100">{faqHeading.subtitle}</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-6">
                <Accordion type="single" collapsible className="w-full">
                  {faqData.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        </div>
        </div>

        {/* Footer */}
        <Footer onShowBlog={() => {
          navigate(getLocalizedPath('/blog', currentLanguage));
        }} />
      </main>
    </>
  );
}
